"use client"
import { type CSSProperties } from "react"
import cn from "classnames"

import { AnimateText } from "~/components/elements/AnimateText/AnimateText"
import { FadeIn } from "~/components/elements/FadeIn/FadeIn"
import { Button } from "~/components/primitives/Button/Button"
import { HtmlRenderer } from "~/components/primitives/HtmlRenderer"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type {
  CapabilitiesInterface,
  CapabilityServiceItem,
} from "./Capabilities.interface"
import s from "./Capabilities.module.scss"

export type CapabilitiesViewProps = Omit<CapabilitiesInterface, "id"> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
}

export const CapabilitiesView = ({
  sectionHeader,
  serviceItems,
  cta,
  config,
  moduleIndex,
  className,
  style,
}: CapabilitiesViewProps) => {
  const configProps = getModuleConfigProps(config)

  return (
    <section
      className={cn(s.capabilities, `stack-module-${moduleIndex}`, className)}
      data-module="capabilities"
      {...configProps}
      style={{ ...configProps.style, ...style }}
    >
      <div className={cn("container", "default-grid")}>
        <div className={cn(s.inner)}>
          {sectionHeader && (
            <AnimateText element="p" className={s.sectionHeader}>
              {sectionHeader}
            </AnimateText>
          )}

          <ul className={s.serviceList}>
            {serviceItems.map((item, index) => (
              <li key={index} className={s.serviceItem}>
                <FadeIn delay={index * 0.08}>
                  <ServiceRow item={item} />
                </FadeIn>
              </li>
            ))}
          </ul>

          {cta?.url && (
            <FadeIn
              className={s.ctaWrapper}
              delay={serviceItems.length * 0.08 + 0.1}
            >
              <Button
                cta={{ ...cta, variant: "tertiary" }}
                theme={configProps["data-typography"] || "light"}
                className={s.cta}
              />
            </FadeIn>
          )}
        </div>
      </div>
    </section>
  )
}

const ServiceRow = ({ item }: { item: CapabilityServiceItem }) => {
  const content = (
    <div className={s.rowContent}>
      <div className={s.titleWrap} data-element="item-title">
        <span className={s.rollVisible} aria-hidden="false">
          {item.title}
        </span>
        <span className={s.rollHidden} aria-hidden="true">
          {item.title}
        </span>
      </div>

      {item.description && (
        <span className={s.description} data-element="item-description">
          <HtmlRenderer html={item.description} element="span" />
        </span>
      )}
    </div>
  )

  if (item.cta?.url) {
    return (
      <a
        href={item.cta.url}
        target={item.cta.target || undefined}
        rel={item.cta.target === "_blank" ? "noopener noreferrer" : undefined}
        className={s.serviceRow}
        aria-label={
          item.description
            ? `${item.title}: ${item.description.replace(/<[^>]*>/g, "")}`
            : item.title
        }
      >
        {content}
      </a>
    )
  }

  return <div className={s.serviceRow}>{content}</div>
}
